import { Suspense, useCallback, useEffect, useRef, useState } from "react";

import { Input } from "reactstrap";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useUserProducts } from "../../hooks/ProductHooks";
import debounce from "lodash/debounce";
import GenericFlyout from "../modal/GenericFlyout";
import ProductListItem from "../ProductListItem";
import { productSimpleSearch } from "../../utils/ProductsUtils";
import { useMediaQuery } from "react-responsive";
import Icon from "../Icon";
import useIsMounted from "../../hooks/useIsMounted";

const B2BSearchInner = () => {
  const t = useBjelinTranslations();
  const products = useUserProducts();
  const isMounted = useIsMounted();

  const [query, setQuery] = useState("");
  const [activeSKU, setActiveSKU] = useState(0);
  const input = useRef();
  const itemsRef = useRef({});

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const closeSearchDropdown = useCallback(
    () => setShowSearchDropdown(false),
    []
  );

  useEffect(() => {
    const res = productSimpleSearch(products, query);
    setFilteredProducts(res);
    if (res?.length > 0) setShowSearchDropdown(true);
  }, [products, query]);

  const updateQuery = useCallback(e => {
    setActiveSKU(0);
    setQuery(e.target.value);
  }, []);

  const updateQueryDelayed = useCallback(
    e => {
      setTimeout(() => {
        updateQuery(e);
      }, 1);
    },
    [updateQuery]
  );

  const closeSearchResults = useCallback(() => {
    setActiveSKU(0);
    setShowSearchDropdown(false);
    input.current.focus();
  }, []);

  const focusInput = useCallback(() => {
    setActiveSKU(0);
    input.current.focus();
  }, []);

  const setActive = useCallback(sku => {
    setActiveSKU(sku);
  }, []);

  const handleKeyDown = useCallback(
    e => {
      switch (e.key) {
        case "Escape":
          setActiveSKU(0);
          setShowSearchDropdown(false);
          input.current.value = "";
          break;
        case "Enter":
          if (!showSearchDropdown) setShowSearchDropdown(true);
          if (filteredProducts.length > 0) {
            if (!activeSKU) {
              const prod = filteredProducts[0];
              setActiveSKU(prod.sku);
            } else {
              itemsRef.current[activeSKU].focusInput();
            }
          }
          break;
        case "ArrowDown":
        case "Tab":
          if (filteredProducts.length > 0) {
            const activeIndex = activeSKU
              ? filteredProducts.findIndex(p => p.sku === activeSKU)
              : -1;
            const prod =
              filteredProducts[(activeIndex + 1) % filteredProducts.length];
            setActiveSKU(prod.sku);
          }
          e.preventDefault();
          break;
        case "ArrowUp":
          if (!showSearchDropdown) return;

          if (filteredProducts.length > 0) {
            const activeIndex = activeSKU
              ? filteredProducts.findIndex(p => p.sku === activeSKU)
              : -1;
            const prod =
              filteredProducts[
                (activeIndex + filteredProducts.length - 1) %
                  filteredProducts.length
              ];
            setActiveSKU(prod.sku);
          }
          e.preventDefault();
          break;
        case "ArrowLeft":
        case "ArrowRight":
          break;
        case "Backspace":
        case "Delete":
        default:
          if (activeSKU && filteredProducts.length > 0) {
            //Entering numbers will focus the amount input box
            const amount = parseInt(e.key);
            if (amount >= 1 && amount <= 9) {
              if (itemsRef.current[activeSKU]) {
                itemsRef.current[activeSKU].focusInput(parseInt(amount));
              }
              e.preventDefault();
              return;
            }
          }

          debounce(updateQuery, 100)(e);
      }
    },
    [activeSKU, filteredProducts, showSearchDropdown, updateQuery]
  );

  useEffect(() => {
    setTimeout(() => {
      if (isMounted() && input.current) {
        input.current.focus();
      }
    }, 1);
  }, [isMounted]);

  return (
    <>
      <Input
        type={"text"}
        id="input-search-query"
        className={"input-search-query"}
        name="query"
        autoComplete="false"
        placeholder={t("search.field-label")}
        innerRef={input}
        onKeyDown={handleKeyDown}
        onPaste={updateQueryDelayed}
        onCut={updateQueryDelayed}
        onClick={() => {
          setActiveSKU(0);
          setShowSearchDropdown(true);
        }}
      />
      <GenericFlyout
        id="search-dropdown"
        isOpen={showSearchDropdown}
        close={closeSearchDropdown}
        excludeFromClose="input-search-query"
        align={"top-center"}
        width={52}
        noCloseBtn
        ariaLabel={t("search.results")}
      >
        {showSearchDropdown && (
          <div
            style={{
              height: "auto",
              maxHeight: "var(--content-height)",
              background: "#fff",
              overflowY: "auto",
              overflowX: "hidden"
            }}
          >
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <ProductListItem
                  key={product.sku}
                  product={product}
                  b2b
                  searchResultMode
                  active={activeSKU === product.sku}
                  closeParent={closeSearchResults}
                  focusSearchBox={focusInput}
                  setActive={setActive}
                  ref={el => (itemsRef.current[product.sku] = el)}
                  className="b2b"
                />
              ))
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "4em"
                }}
              >
                <div>
                  {" "}
                  <Icon name="circle-info" />{" "}
                  {query.length > 2 ? (
                    <span>{t("search.b2b.no-match")}</span>
                  ) : (
                    <span>
                      {t("search.field-label")}. {t("search.hint")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </GenericFlyout>
    </>
  );
};

const B2BSearch = () => {
  const isSmall = useMediaQuery({ maxWidth: 575.98 });

  return (
    <div
      style={{
        flex: "0 1 100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "4rem",
        whiteSpace: "nowrap",
        textDecoration: "none",
        padding: isSmall ? "0 0" : "0 2rem",
        pointerEvents: "all"
      }}
    >
      <Suspense
        fallback={
          <div
            id="fake-input-search-query"
            className="form-control input-search-query"
            aria-busy
          />
        }
      >
        <B2BSearchInner />
      </Suspense>
    </div>
  );
};

export default B2BSearch;
